import {
  ref, watch, Ref, computed,
} from '@vue/composition-api';
import { FormattedProduct } from '@fc/app-common/src/services/Shopify/types';
import { VARIANT_IDS } from '~/constants/upsellConstants';
import { FullShopifyProductWithVariant } from '~/store/actions/shopify';

interface UpsellProduct extends FormattedProduct {
  value?: string;
  cta: string;
  isDisabled?: boolean;
}

const normalizeTitle = (value: string) => value
  .toLowerCase()
  .replace(/fightcamp/g, '')
  .replace(/[^a-z0-9\s]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const matchesFuzzyTitle = (variantTitle: string, addedTitle: string) => {
  const normalizedVariant = normalizeTitle(variantTitle);
  const normalizedAdded = normalizeTitle(addedTitle);

  if (!normalizedVariant || !normalizedAdded) {
    return false;
  }

  if (normalizedVariant.includes(normalizedAdded) || normalizedAdded.includes(normalizedVariant)) {
    return true;
  }

  const addedTokens = normalizedAdded.split(' ').filter(token => token.length >= 3);
  return addedTokens.some(token => normalizedVariant.includes(token));
};

export function useUpsellItems(
  upsellProducts: Ref<UpsellProduct[]>,
  productsAdded: Ref<FullShopifyProductWithVariant[]>,
) {
  const upsellItems = ref<UpsellProduct[]>([]);

  function computeUpsellItems() {
    const { $store } = useNuxtApp();
    let items = [...upsellProducts.value];

    const isCanada = computed(() => $store.getters.isGeoLocatedCanada);

    // Filter out bag sleeve and floor mats if Canada order
    if (isCanada.value) {
      items = items.filter(item => {
        const firstVariant = item?.variants?.[0];
        if (!firstVariant?.id) return true;
        return !VARIANT_IDS.SOUND_SLEEVE.includes(firstVariant.id)
          && !VARIANT_IDS.RUBBER_FLOORING.includes(firstVariant.id);
      });
    }

    // Extract variant IDs and titles from added products
    const addedVariantIds = new Set(
      productsAdded.value
        .map(product => product?.variant?.id)
        .filter((id): id is string => !!id),
    );
    const addedTitles = productsAdded.value
      .map(product => product?.title || product?.productName)
      .filter((title): title is string => !!title);

    // Filter out upsell items if any of their variant ids or titles are in productsAdded
    items = items.filter(item => {
      if (!item?.variants || !Array.isArray(item.variants)) return false;
      const hasMatchingVariantId = item.variants.some(variant => variant?.id && addedVariantIds.has(variant.id));
      const matchingTitle = item.title && addedTitles.find(addedTitle => matchesFuzzyTitle(item.title, addedTitle));

      if (hasMatchingVariantId || matchingTitle) {
        return false;
      }

      return true;
    });

    upsellItems.value = items.slice(0, 2);
  }

  function updateUpsellItems(id: string, cta: string, isDisabled: boolean) {
    upsellItems.value = upsellItems.value.map((item: any) => {
      if (item?.variants && Array.isArray(item.variants) && item.variants.some((variant: any) => variant?.id === id)) {
        return {
          ...item,
          cta,
          isDisabled,
        };
      }
      return item;
    });
  }

  // Watch for changes and recompute
  watch([upsellProducts], computeUpsellItems, { immediate: true });

  return {
    upsellItems,
    updateUpsellItems,
    computeUpsellItems,
  };
}
